import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  CollectionReference,
  DocumentData,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Technician, TechnicianEarning, TechnicianStats, TechnicianSalaryType } from '../utils/types';

// التحقق من توفر خدمة Firestore
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('خدمة Firestore غير متوفرة');
  }
  return db;
};

// الحصول على مرجع مجموعة الفنيين
const getTechniciansCollection = (): CollectionReference<DocumentData> => {
  const firestore = ensureFirestore();
  return collection(firestore, 'technicians');
};

// الحصول على مرجع مجموعة أجور الفنيين
const getTechnicianEarningsCollection = (): CollectionReference<DocumentData> => {
  const firestore = ensureFirestore();
  return collection(firestore, 'technicianEarnings');
};

// ***** إدارة الفنيين *****

// إضافة فني جديد
export const addTechnician = async (technicianData: Omit<Technician, 'id' | 'createdAt' | 'updatedAt'>): Promise<Technician> => {
  try {
    const techniciansCollection = getTechniciansCollection();
    
    const newTechnician = {
      ...technicianData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(techniciansCollection, newTechnician);
    
    return {
      id: docRef.id,
      ...technicianData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error adding technician:', error);
    throw new Error('فشل في إضافة الفني');
  }
};

// الحصول على جميع الفنيين
export const getAllTechnicians = async (activeOnly: boolean = false): Promise<Technician[]> => {
  try {
    const techniciansCollection = getTechniciansCollection();

    // استعلام بسيط بدون ترتيب لتجنب الحاجة للفهرس المركب
    let q;
    if (activeOnly) {
      q = query(techniciansCollection, where('isActive', '==', true));
    } else {
      q = query(techniciansCollection);
    }

    const querySnapshot = await getDocs(q);
    
    const technicians = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        specialization: data.specialization,
        salaryType: data.salaryType,
        salaryAmount: data.salaryAmount,
        isActive: data.isActive,
        branchId: data.branchId,
        branchName: data.branchName,
        hireDate: data.hireDate instanceof Timestamp ? data.hireDate.toDate() : new Date(data.hireDate),
        notes: data.notes,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as Technician;
    });

    // ترتيب الفنيين حسب الاسم بعد جلب البيانات
    technicians.sort((a, b) => a.name.localeCompare(b.name, 'ar'));

    return technicians;
  } catch (error) {
    console.error('Error getting technicians:', error);
    throw new Error('فشل في جلب بيانات الفنيين');
  }
};

// الحصول على فني بالمعرف
export const getTechnicianById = async (id: string): Promise<Technician | null> => {
  try {
    const technicianRef = doc(getTechniciansCollection(), id);
    const technicianDoc = await getDoc(technicianRef);
    
    if (!technicianDoc.exists()) {
      return null;
    }
    
    const data = technicianDoc.data();
    return {
      id: technicianDoc.id,
      name: data.name,
      phone: data.phone,
      email: data.email,
      specialization: data.specialization,
      salaryType: data.salaryType,
      salaryAmount: data.salaryAmount,
      isActive: data.isActive,
      branchId: data.branchId,
      branchName: data.branchName,
      hireDate: data.hireDate instanceof Timestamp ? data.hireDate.toDate() : new Date(data.hireDate),
      notes: data.notes,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt)
    } as Technician;
  } catch (error) {
    console.error('Error getting technician by ID:', error);
    throw new Error('فشل في جلب بيانات الفني');
  }
};

// تحديث فني
export const updateTechnician = async (id: string, updates: Partial<Technician>): Promise<void> => {
  try {
    const technicianRef = doc(getTechniciansCollection(), id);
    
    await updateDoc(technicianRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating technician:', error);
    throw new Error('فشل في تحديث بيانات الفني');
  }
};

// حذف فني
export const deleteTechnician = async (id: string): Promise<void> => {
  try {
    const technicianRef = doc(getTechniciansCollection(), id);
    await deleteDoc(technicianRef);
  } catch (error) {
    console.error('Error deleting technician:', error);
    throw new Error('فشل في حذف الفني');
  }
};

// ***** حساب أجور الفنيين *****

// حساب أجر الفني لطلب صيانة
export const calculateTechnicianEarning = (
  technician: Technician,
  maintenanceCost: number,
  customAmount?: number
): { earningAmount: number; calculationDetails: string } => {
  let earningAmount = 0;
  let calculationDetails = '';

  switch (technician.salaryType) {
    case TechnicianSalaryType.FIXED:
      earningAmount = technician.salaryAmount;
      calculationDetails = `راتب ثابت: ${technician.salaryAmount} جنيه`;
      break;

    case TechnicianSalaryType.PER_DEVICE:
      earningAmount = technician.salaryAmount;
      calculationDetails = `أجر بالقطعة: ${technician.salaryAmount} جنيه لكل جهاز`;
      break;

    case TechnicianSalaryType.PERCENTAGE:
      earningAmount = (maintenanceCost * technician.salaryAmount) / 100;
      calculationDetails = `نسبة ${technician.salaryAmount}% من ${maintenanceCost} = ${earningAmount} جنيه`;
      break;

    case TechnicianSalaryType.VARIABLE:
      if (customAmount && customAmount > 0) {
        earningAmount = customAmount;
        calculationDetails = `مبلغ متغير مخصص: ${customAmount} جنيه`;
      } else if (technician.salaryAmount > 0) {
        earningAmount = technician.salaryAmount;
        calculationDetails = `مبلغ متغير افتراضي: ${technician.salaryAmount} جنيه`;
      } else {
        // حساب نسبة افتراضية إذا لم يوجد مبلغ
        earningAmount = maintenanceCost * 0.15; // 15% من التكلفة
        calculationDetails = `مبلغ متغير (15% من التكلفة): ${earningAmount.toFixed(2)} جنيه`;
      }
      break;

    default:
      earningAmount = 0;
      calculationDetails = 'نوع الراتب غير محدد';
  }

  return { earningAmount, calculationDetails };
};

// تسجيل أجر فني لطلب صيانة
export const recordTechnicianEarning = async (
  technicianId: string,
  maintenanceId: string,
  deviceType: string,
  deviceSize: string,
  maintenanceCost: number,
  branchId: string,
  customAmount?: number
): Promise<TechnicianEarning> => {
  try {
    console.log('💾 بدء تسجيل أجر الفني:', {
      technicianId,
      maintenanceId,
      customAmount,
      maintenanceCost
    });

    // الحصول على بيانات الفني
    const technician = await getTechnicianById(technicianId);
    if (!technician) {
      throw new Error('الفني غير موجود');
    }

    // التحقق من وجود سجل سابق لنفس الطلب
    const earningsCollection = getTechnicianEarningsCollection();
    const existingQuery = query(
      earningsCollection,
      where('technicianId', '==', technicianId),
      where('maintenanceId', '==', maintenanceId)
    );

    const existingSnapshot = await getDocs(existingQuery);

    // حساب الأجر
    const { earningAmount, calculationDetails } = calculateTechnicianEarning(
      technician,
      maintenanceCost,
      customAmount
    );

    console.log('💰 تفاصيل الأجر المحسوب:', {
      earningAmount,
      calculationDetails,
      salaryType: technician.salaryType
    });

    // إنشاء سجل الأجر
    const earningData = {
      technicianId,
      technicianName: technician.name,
      maintenanceId,
      deviceType,
      deviceSize,
      salaryType: technician.salaryType,
      baseAmount: maintenanceCost,
      earningAmount,
      calculationDetails,
      date: new Date(),
      branchId,
      createdAt: serverTimestamp()
    };

    // حفظ أو تحديث السجل
    let docRef;
    if (existingSnapshot.docs.length > 0) {
      // تحديث السجل الموجود
      const existingDoc = existingSnapshot.docs[0];
      await updateDoc(doc(earningsCollection, existingDoc.id), {
        ...earningData,
        updatedAt: serverTimestamp()
      });
      docRef = { id: existingDoc.id };
      console.log('🔄 تم تحديث سجل الأجر الموجود');
    } else {
      // إنشاء سجل جديد
      docRef = await addDoc(earningsCollection, earningData);
      console.log('✅ تم إنشاء سجل أجر جديد');
    }

    return {
      id: docRef.id,
      ...earningData,
      date: new Date(),
      createdAt: new Date()
    } as TechnicianEarning;
  } catch (error) {
    console.error('Error recording technician earning:', error);
    throw new Error('فشل في تسجيل أجر الفني');
  }
};

// الحصول على أجور فني في فترة محددة
export const getTechnicianEarnings = async (
  technicianId: string,
  startDate: Date,
  endDate: Date
): Promise<TechnicianEarning[]> => {
  try {
    console.log('💰 جلب أجور الفني:', {
      technicianId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    const earningsCollection = getTechnicianEarningsCollection();

    const q = query(
      earningsCollection,
      where('technicianId', '==', technicianId),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    console.log('📋 عدد سجلات الأجور:', querySnapshot.size);

    const earnings = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        technicianId: data.technicianId,
        technicianName: data.technicianName,
        maintenanceId: data.maintenanceId,
        deviceType: data.deviceType,
        deviceSize: data.deviceSize,
        salaryType: data.salaryType,
        baseAmount: data.baseAmount,
        earningAmount: data.earningAmount,
        calculationDetails: data.calculationDetails,
        date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
        branchId: data.branchId,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt)
      } as TechnicianEarning;
    });

    console.log('✅ تم جلب أجور الفني بنجاح:', earnings.length, 'سجل');
    return earnings;
  } catch (error) {
    console.error('❌ خطأ في جلب أجور الفني:', error);
    throw new Error('فشل في جلب أجور الفني');
  }
};

// حساب إحصائيات الفني
export const getTechnicianStats = async (
  technicianId: string,
  startDate: Date,
  endDate: Date
): Promise<TechnicianStats> => {
  try {
    console.log('📊 حساب إحصائيات الفني:', {
      technicianId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    const technician = await getTechnicianById(technicianId);
    if (!technician) {
      console.error('❌ الفني غير موجود:', technicianId);
      throw new Error('الفني غير موجود');
    }

    console.log('👨‍🔧 بيانات الفني:', technician.name);
    const earnings = await getTechnicianEarnings(technicianId, startDate, endDate);
    console.log('💰 أجور الفني:', earnings.length, 'سجل');

    const totalDevices = earnings.length;
    const totalEarnings = earnings.reduce((sum, earning) => sum + earning.earningAmount, 0);
    const averageEarningPerDevice = totalDevices > 0 ? totalEarnings / totalDevices : 0;

    // تجميع الأجهزة حسب النوع
    const devicesByType: Record<string, number> = {};
    earnings.forEach(earning => {
      devicesByType[earning.deviceType] = (devicesByType[earning.deviceType] || 0) + 1;
    });

    // تجميع الأجور حسب التاريخ
    const earningsByDate: Record<string, number> = {};
    earnings.forEach(earning => {
      const date = earning.date instanceof Date ? earning.date : new Date(earning.date);
      const dateStr = date.toISOString().split('T')[0];
      earningsByDate[dateStr] = (earningsByDate[dateStr] || 0) + earning.earningAmount;
    });

    return {
      technicianId,
      technicianName: technician.name,
      totalDevices,
      totalEarnings,
      averageEarningPerDevice,
      devicesByType,
      earningsByDate,
      period: {
        startDate,
        endDate
      }
    };
  } catch (error) {
    console.error('Error getting technician stats:', error);
    throw new Error('فشل في حساب إحصائيات الفني');
  }
};

// الحصول على جميع الفنيين حسب الفرع
export const getTechniciansByBranch = async (branchId: string, activeOnly: boolean = true): Promise<Technician[]> => {
  try {
    console.log('🔍 البحث عن فنيين في الفرع:', branchId, 'نشطين فقط:', activeOnly);
    const techniciansCollection = getTechniciansCollection();

    let q;
    if (activeOnly) {
      q = query(
        techniciansCollection,
        where('branchId', '==', branchId),
        where('isActive', '==', true)
      );
    } else {
      q = query(
        techniciansCollection,
        where('branchId', '==', branchId)
      );
    }

    const querySnapshot = await getDocs(q);
    console.log('📊 عدد الفنيين الموجودين:', querySnapshot.docs.length);

    const technicians = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        specialization: data.specialization,
        salaryType: data.salaryType,
        salaryAmount: data.salaryAmount,
        isActive: data.isActive,
        branchId: data.branchId,
        branchName: data.branchName,
        hireDate: data.hireDate instanceof Timestamp ? data.hireDate.toDate() : new Date(data.hireDate),
        notes: data.notes,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt)
      } as Technician;
    });

    // ترتيب الفنيين حسب الاسم
    technicians.sort((a, b) => a.name.localeCompare(b.name, 'ar'));

    console.log('👨‍🔧 الفنيين المرجعين:', technicians);
    return technicians;
  } catch (error) {
    console.error('❌ خطأ في جلب فنيي الفرع:', error);
    console.error('تفاصيل الخطأ:', {
      branchId,
      activeOnly,
      errorMessage: error instanceof Error ? error.message : 'خطأ غير معروف'
    });
    throw new Error('فشل في جلب فنيي الفرع');
  }
};

// حذف أجر فني
export const deleteTechnicianEarning = async (earningId: string): Promise<void> => {
  try {
    const earningRef = doc(getTechnicianEarningsCollection(), earningId);
    await deleteDoc(earningRef);
  } catch (error) {
    console.error('Error deleting technician earning:', error);
    throw new Error('فشل في حذف أجر الفني');
  }
};

// التحقق من وجود فنيين في فرع معين
export const checkTechniciansInBranch = async (branchId: string): Promise<{ count: number; activeCount: number }> => {
  try {
    console.log('🔍 التحقق من فنيي الفرع:', branchId);
    const techniciansCollection = getTechniciansCollection();

    // عد جميع الفنيين
    const allQuery = query(techniciansCollection, where('branchId', '==', branchId));
    const allSnapshot = await getDocs(allQuery);

    // عد الفنيين النشطين
    const activeQuery = query(
      techniciansCollection,
      where('branchId', '==', branchId),
      where('isActive', '==', true)
    );
    const activeSnapshot = await getDocs(activeQuery);

    const result = {
      count: allSnapshot.docs.length,
      activeCount: activeSnapshot.docs.length
    };

    console.log('📊 إحصائيات فنيي الفرع:', result);
    return result;
  } catch (error) {
    console.error('❌ خطأ في التحقق من فنيي الفرع:', error);
    return { count: 0, activeCount: 0 };
  }
};
